import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { I18n } from 'galarm-config'
import { Constants } from 'galarm-config'
import { ActionCreators, Selectors, AlarmUtils } from 'galarm-shared'
import objGet from 'lodash/get'
import sortBy from 'lodash/sortBy'
import { Box, List, Button, Card, CardBody, Text } from 'grommet'
import AlarmsByCategorySummary from './AlarmsByCategorySummary'
import findIndex from 'lodash/findIndex'
import { SecondaryText } from 'web-components'

const { calendarEventsSelector } = Selectors

const CalendarEventsCategorySummary = ({
  calendarEvents,
  onChooseCategory
}) => {
  const colorScheme = useSelector(state => state.userSettings.webColorScheme)

  const calendarCategoryColor = AlarmUtils.getColorForAlarmCategory(
    colorScheme,
    'calendarYellow'
  )

  return (
    <Card background={calendarCategoryColor}>
      <CardBody direction="row" pad="small">
        <Box flex align="center" gap="small" onClick={onChooseCategory}>
          <Text color="textColor">{I18n.t('calendarEvents')}</Text>
          <SecondaryText color="textColor">
            {I18n.t('numEvents', {
              count: calendarEvents.length
            })}
          </SecondaryText>
        </Box>
      </CardBody>
    </Card>
  )
}

const {
  makeAlarmsInCategorySelector,
  alarmCategoriesSelector,
  incidentAlarmsSelector,
  instantAlarmsSelector,
  taskAlarmsSelector
} = Selectors

const EmptyAlarmsByCategorySummary = () => {
  return <Box pad="small" margin="small" />
}

const TasksCategorySummary = ({ taskAlarms, onChooseAlarmCategory }) => {
  const colorScheme = useSelector(state => state.userSettings.webColorScheme)
  const color = AlarmUtils.getColorForAlarmCategory(colorScheme, 'seaGreen')
  return (
    <Card background={color}>
      <CardBody direction="row" pad="small">
        <Box flex align="center" gap="small" onClick={onChooseAlarmCategory}>
          <Text color="textColor">{I18n.t('checklists')}</Text>
          <SecondaryText color="textColor">
            {I18n.t('numAlarms', {
              count: taskAlarms.length
            })}
          </SecondaryText>
        </Box>
      </CardBody>
    </Card>
  )
}

const AlertIncidentsCategorySummary = ({
  alertIncidents,
  onChooseAlarmCategory
}) => {
  return (
    <Card background="red">
      <CardBody direction="row" pad="small">
        <Box flex align="center" gap="small" onClick={onChooseAlarmCategory}>
          <Text color="textColor">{I18n.t('alertIncidents')}</Text>
          <SecondaryText color="textColor">
            {I18n.t('numIncidents', {
              count: alertIncidents.length
            })}
          </SecondaryText>
        </Box>
      </CardBody>
    </Card>
  )
}

const InstantAlarmsCategorySummary = ({
  instantAlarms,
  onChooseAlarmCategory
}) => {
  const colorScheme = useSelector(state => state.userSettings.webColorScheme)
  const color = AlarmUtils.getColorForAlarmCategory(colorScheme, 'skyBlue')
  return (
    <Card background={color}>
      <CardBody direction="row" pad="small">
        <Box flex align="center" gap="small" onClick={onChooseAlarmCategory}>
          <Text color="textColor">{I18n.t('instantAlarms')}</Text>
          <SecondaryText color="textColor">
            {I18n.t('numAlarms', {
              count: instantAlarms.length
            })}
          </SecondaryText>
        </Box>
      </CardBody>
    </Card>
  )
}

const AlarmsByCategoriesList = () => {
  const dispatch = useDispatch()

  const alarmsInCategorySelector = makeAlarmsInCategorySelector()

  const [showHiddenCategories, setShowHiddenCategories] = useState(false)

  const alarmCategories = useSelector(alarmCategoriesSelector)
  const numUncategorizedAlarms = useSelector(
    state =>
      alarmsInCategorySelector(state, {
        id: Constants.UNCATEGORIZED_ALARM_CATEGORY_ID
      }).length
  )

  const sortAlarmCategoriesBy = useSelector(
    state => state.userSettings.sortAlarmCategoriesBy
  )

  const calendarEvents = useSelector(calendarEventsSelector)
  const onChooseCalendarEventsCategory = () => {
    dispatch(ActionCreators.setShowCalendarEvents(true))
  }

  const incidentAlarms = useSelector(incidentAlarmsSelector)
  const onChooseAlertIncidentsCategory = () => {
    console.log('onChooseAlertIncidentsCategory')
    dispatch(ActionCreators.setShowAlertIncidents(true))
  }

  const instantAlarms = useSelector(instantAlarmsSelector)
  const onChooseInstantAlarmsCategory = () => {
    dispatch(ActionCreators.setShowInstantAlarms(true))
  }

  const taskAlarms = useSelector(taskAlarmsSelector)
  const onChooseTasksCategory = () => {
    dispatch(ActionCreators.setShowTasks(true))
  }

  let sortedAlarmCategories = alarmCategories

  if (
    sortAlarmCategoriesBy === Constants.SORT_ALARM_CATEGORIES_OPTIONS.BY_NAME
  ) {
    sortedAlarmCategories = sortBy(Object.values(alarmCategories), ['name'])
  } else {
    sortedAlarmCategories = Object.values(alarmCategories).sort(
      (alarmCategory1, alarmCategory2) => {
        const numAlarmsAlarmCategory1 = objGet(
          alarmCategory1,
          'alarmIds',
          []
        ).length
        const numAlarmsAlarmCategory2 = objGet(
          alarmCategory2,
          'alarmIds',
          []
        ).length
        return numAlarmsAlarmCategory2 - numAlarmsAlarmCategory1
      }
    )
  }

  if (numUncategorizedAlarms === 0) {
    const uncategorizedAlarmCategoryIndex = sortedAlarmCategories.findIndex(
      alarmCategory =>
        alarmCategory.id === Constants.UNCATEGORIZED_ALARM_CATEGORY_ID
    )
    sortedAlarmCategories.splice(uncategorizedAlarmCategoryIndex, 1)
  }

  const numDisabledCategories = sortedAlarmCategories.filter(
    sortedAlarmCategory => sortedAlarmCategory.disabled
  ).length

  if (!showHiddenCategories) {
    sortedAlarmCategories = sortedAlarmCategories.filter(
      sortedAlarmCategory => !sortedAlarmCategory.disabled
    )
  } else {
    const indexOfFirstHiddenCategory = findIndex(
      sortedAlarmCategories,
      sortedAlarmCategory => sortedAlarmCategory.disabled
    )
    if (indexOfFirstHiddenCategory !== -1) {
      // Add a section header Hidden alarm categories before starting to show the hidden categories
      sortedAlarmCategories.splice(indexOfFirstHiddenCategory, 0, {
        sectionHeader: true,
        name: I18n.t('hiddenAlarmCategories')
      })
    }
  }

  sortedAlarmCategories.unshift({
    type: 'instantAlarms',
    instantAlarms: instantAlarms
  })

  if (taskAlarms.length > 0) {
    sortedAlarmCategories.unshift({
      type: 'tasks',
      taskAlarms: taskAlarms
    })
  }

  if (incidentAlarms.length > 0) {
    sortedAlarmCategories.unshift({
      type: 'alertIncidents',
      alertIncidents: incidentAlarms
    })
  }

  if (calendarEvents.length > 0) {
    sortedAlarmCategories.unshift({
      type: 'calendarEvents',
      calendarEvents: calendarEvents
    })
  }

  return (
    <Box overflow="auto">
      <List
        border={false}
        margin={{ bottom: '10px' }}
        pad={{ vertical: '5px', horizontal: '20px' }}
        data={Object.values(sortedAlarmCategories)}>
        {alarmCategory => {
          if (alarmCategory.type === 'calendarEvents') {
            return (
              <CalendarEventsCategorySummary
                calendarEvents={alarmCategory.calendarEvents}
                onChooseCategory={onChooseCalendarEventsCategory}
              />
            )
          } else if (alarmCategory.type === 'alertIncidents') {
            return (
              <AlertIncidentsCategorySummary
                alertIncidents={alarmCategory.alertIncidents}
                onChooseAlarmCategory={onChooseAlertIncidentsCategory}
              />
            )
          } else if (alarmCategory.type === 'instantAlarms') {
            return (
              <InstantAlarmsCategorySummary
                instantAlarms={alarmCategory.instantAlarms}
                onChooseAlarmCategory={onChooseInstantAlarmsCategory}
              />
            )
          } else if (alarmCategory.type === 'tasks') {
            return (
              <TasksCategorySummary
                taskAlarms={alarmCategory.taskAlarms}
                onChooseAlarmCategory={onChooseTasksCategory}
              />
            )
          } else if (alarmCategory.empty) {
            return <EmptyAlarmsByCategorySummary />
          }

          return <AlarmsByCategorySummary alarmCategory={alarmCategory} />
        }}
      </List>
      {numDisabledCategories > 0 && (
        <Button
          plain
          alignSelf="center"
          margin={{ horizontal: '20px', bottom: '20px' }}
          color="primary"
          label={
            showHiddenCategories
              ? I18n.t('dontShowHiddenCategories')
              : I18n.t('showHiddenCategories')
          }
          onClick={() => setShowHiddenCategories(!showHiddenCategories)}
        />
      )}
    </Box>
  )
}

export default AlarmsByCategoriesList
